import { useState, useRef, useEffect } from "react";
import {
  Tree,
  getDescendants,
  getBackendOptions,
  MultiBackend,
} from "@minoru/react-dnd-treeview";
import { DndProvider } from "react-dnd";
import { Tag, InlineEdit, Input, IconButton, Whisper, Tooltip, Dropdown, Popover, Badge, Toggle, Button, Progress } from "rsuite";

import TrashIcon from '@rsuite/icons/Trash';
import MenuIcon from '@rsuite/icons/Menu';
import ArrowDownLineIcon from '@rsuite/icons/ArrowDownLine';
import AttachmentIcon from '@rsuite/icons/Attachment';
import EditIcon from '@rsuite/icons/Edit';
import CheckIcon from '@rsuite/icons/Check';

import CollaspedFillIcon from '@rsuite/icons/CollaspedFill';
import ExpandOutlineIcon from '@rsuite/icons/ExpandOutline';

import FolderFillIcon from '@rsuite/icons/FolderFill';
import PageIcon from '@rsuite/icons/Page';

import CopyIcon from '@rsuite/icons/Copy';

import { deleteBriefSection, updateBriefSection, updateBriefSectionBatch, createParameter, createSection, getFileStatus, getFileHashById, downloadZIP } from "../briefCreationManager";
import { formatTimestamp } from '../../../utils/formatTimestamp';

function computeNumbers(items) {
  const numbers = {};
  const rootItems = items.filter(i => i.parent === 0).sort((a, b) => {
    if (a.data.index !== b.data.index) return a.data.index - b.data.index;
    return a.data.updated_at > b.data.updated_at ? -1 : 1;
  });
  rootItems.forEach((item, idx) => {
    const n = idx + 1;
    numbers[item.id] = String(n);
    if (item.droppable) {
      const children = items.filter(i => i.parent === item.id).sort((a, b) => {
        if (a.data.index !== b.data.index) return a.data.index - b.data.index;
        return a.data.updated_at > b.data.updated_at ? -1 : 1;
      });
      children.forEach((child, cIdx) => {
        numbers[child.id] = `${n}.${cIdx + 1}`;
      });
    }
  });
  return numbers;
}

const ITEM_TYPES = {
    section: {
        label: 'Раздел',
        color: 'green',
    },
    parameter: {
        label: 'Параметр',
        color: 'yellow',
    },
};

const FILE_TYPES = {
    pdf: {
        label: 'PDF',
        color: 'red',
    },
    docx: {
        label: 'DOCX',
        color: 'blue',
    },
    doc: {
        label: 'DOC',
        color: 'blue',
    },
    xls: {
        label: 'XLS',
        color: 'green',
    },
    xlsx: {
        label: 'XLSX',
        color: 'green',
    },
    pptx: {
        label: 'PPTX',
        color: 'orange',
    },
    ppt: {
        label: 'PPT',
        color: 'orange',
    }
}

const getLastId = (treeData) => {
  const reversedArray = [...treeData].sort((a, b) => {
    if (a.id < b.id) {
      return 1;
    } else if (a.id > b.id) {
      return -1;
    }

    return 0;
  });

  if (reversedArray.length > 0) {
    return reversedArray[0].id;
  }

  return 0;
};

const customDragPreview = (monitorProps) => {
    return (
        <div></div>
    )
}

export const Placeholder = (node, depth) => (
    <div
      style={{ left: 2 * depth + "rem", height: "0.15rem", backgroundColor: "lightgray" }}
      data-testid="placeholder"
    ></div>
  );

//returns filename and extension in array
const getFilenameAndExtension = (filename) => {
  return [filename.split('.').slice(0, -1).join('.'), filename.split('.').pop()];
}

const UploadedFiles = ({file}) => {
  const [filename, extension] = getFilenameAndExtension(file.filename);

  console.log(filename, extension);
  return (
    <div className="uploaded-file">
      <div className="uploaded-file-box">
        <div className="uploaded-file-name">{filename}</div>
        <Tag style={{marginLeft: "0.5rem"}} className="uploaded-file-extension" color={FILE_TYPES[extension]?.color}>{extension}</Tag>
        {file.uploaded_at && (
          <span style={{fontSize: '0.75em', color: 'var(--rs-text-secondary)', marginLeft: '0.5rem'}}>
            {formatTimestamp(file.uploaded_at)}
          </span>
        )}
      </div>
      <div style={{display: "flex", alignItems: "center", width: "50%", justifyContent: "end"}}>
        { file.status && (file.status != "DONE" ?
        <Progress showInfo={false} strokeColor="var(--rs-state-success)" style={{width: "10rem", marginRight: "0.5rem"}} percent={100} status="active" />
        :
        <Button style={{marginRight: "0.5rem"}}
        onClick={async () => {
            let hash = await getFileHashById(file.id);
            window.open(`/analytics/${hash}`)
          }}
          color="green" appearance="ghost">
          Посмотреть таблицу
        </Button>)
        }
        <Button onClick={() => window.open(`${process.env.REACT_APP_API_HOST}/api/download?filename=${file.filename}`)} color="green" appearance="primary">
          Скачать
        </Button>
      </div>
    </div>
  )
}
  

const renderTreeNode = (item,
                        onToggle,
                        depth,
                        isOpen,
                        handleRef,
                        handleTextChange, debounce, handleCreate,
                        handleDelete,
                        handleDataChange,
                        numbers) => {
    return (
        <div className={`brief-list-item-box${item.droppable ? ' brief-list-item-section' : ''}`} style={{marginLeft: 2 * depth + "rem"}}>
            <div style={{display: 'flex', alignItems: 'center', width: '100%'}}>
                <div
                    className={`drag-handle`}
                    ref={handleRef}
                    style={{marginRight: '0.5rem'}}
                >
                    <MenuIcon/>
                </div>
                {numbers && numbers[item.id] && (
                  <span style={{
                    minWidth: '2rem',
                    fontWeight: 600,
                    color: 'var(--rs-text-secondary)',
                    marginLeft: '0.4rem',
                    marginRight: '0.35rem',
                    fontSize: '0.85em',
                    flexShrink: 0
                  }}>{numbers[item.id]}</span>
                )}
                <Tag className={"brief-tree-tag"} onClick={onToggle} style={{marginRight: 10, minWidth: '5.5rem'}} color={ITEM_TYPES[item.data.type].color}>
                    <span className={`brief-tree-tag-label ${item.droppable ? "expandable" : ""}`}>{ITEM_TYPES[item.data.type].label}</span>
                    {
                        item.droppable &&
                        <ArrowDownLineIcon className={"brief-tree-tag-icon"} style={{transform: `rotate(${isOpen ? 0 : -90}deg)`}} />
                    }
                </Tag>
                <InlineEdit defaultValue={item.text} onChange={(text) => {
                    debounce(item.id, () => updateBriefSection(item.id, item.data.type, {name: text}), 1000)
                    handleTextChange(text);
                  }}
                  style={{width: '100%'}}>
                    <Input style={{width: '100%'}}/>
                </InlineEdit>

            </div>
            <div style={{marginLeft: '0.25rem', display: 'flex', alignItems: 'center'}}>
                {
                  item.data.type == "parameter" ?
                  <span style={{display: 'flex', alignItems: 'center', marginRight: '0.5rem'}}>
                      <Badge color="green" content={item.data.input_text ? "" : false}
                        style={{marginRight: '0.5rem'}}>
                          <Whisper enterable placement="top" trigger="hover" speaker={
                          item.data.input_text ?
                          <Popover className="rs-uploader-file-items" style={{display: 'flex', flexDirection: 'column'}}>
                            <div className="rs-uploader-file-items-title">Введенное поле</div>
                            <div>
                              {item.data.input_text}
                            </div>
                          </Popover>
                          :
                          <Tooltip>Запросить ввод</Tooltip>
                        }>
                          <EditIcon
                            key={`have_input-${item.id}-${item.data.have_input}`}
                            style={{marginRight: item.data.input_text ? 0 : '0.5rem'}}
                            className={`brief-tree-option-icon ${item.data.have_input ? "active" : ""}`}
                            onClick={() => {
                              updateBriefSection(item.id, item.data.type, {have_input: !item.data.have_input});
                              handleDataChange(item.id, {have_input: !item.data.have_input});
                            }}
                          />
                        </Whisper>
                      </Badge>
                    
                    <Badge
                      color="green"
                      content={item.data.files && item.data.files.length > 0 ? item.data.files.length : false}
                      >
                      <Whisper enterable placement="auto" trigger="hover" speaker={
                        item.data.have_file ?
                        <Popover className="rs-uploader-file-items">
                          <div className="rs-uploader-file-items-wrapper">
                            <div className="rs-uploader-file-items-block">
                              <span className="rs-uploader-file-items-title">Настройки анализа</span>
                              <Toggle checked={item.data.have_ocr}
                              color="green"
                              style={{
                                width: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                padding: "0.5rem"}}
                              onChange={() => {
                                updateBriefSection(item.id, item.data.type, {have_ocr: !item.data.have_ocr});
                                handleDataChange(item.id, {have_ocr: !item.data.have_ocr});
                              }}>
                                Автоматический анализ PDF файлов
                              </Toggle>
                            </div>
                            {
                            item.data.files && item.data.files.length > 0 ?
                            <div className="rs-uploader-file-items-items rs-uploader-file-items-block">
                              <span className="rs-uploader-file-items-title">
                                <span style={{width: '100%'}}>Прикрепленные файлы</span>
                                <Button style={{marginLeft: '0.5rem', paddingInline: '1rem'}} size="xs" onClick={() => downloadZIP(item.id)}>Скачать zip</Button>
                              </span>
                              <div style={{maxHeight: '320px', overflowY: 'auto'}}>
                                {item.data.files.map(file => (
                                  <div className="rs-uploader-file-item">
                                    <div style={{
                                      cursor: 'pointer'
                                    }}
                                      className="rs-uploader-file-item-name">
                                        <UploadedFiles file={file}/>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                            : null}
                          </div>
                        </Popover>
                        :
                        <Tooltip>Запросить файлы</Tooltip>
                        }>
                        
                          <AttachmentIcon
                            className={`brief-tree-option-icon ${item.data.have_file ? "active" : ""}`}
                            onClick={() => {
                              updateBriefSection(item.id, item.data.type, {have_file: !item.data.have_file});
                              handleDataChange(item.id, {have_file: !item.data.have_file});
                            }}
                          />
                        
                      </Whisper>
                    </Badge>
                  </span> :
                  <Whisper placement="top" trigger="hover" speaker={<Tooltip>Добавить параметр</Tooltip>}>
                    <IconButton color="green" appearance="subtle" style={{marginRight: '0.25rem'}} onClick={() => {
                      handleCreate("parameter", item.id)
                    }} icon={<ExpandOutlineIcon />} />
                  </Whisper>
                }
                <Whisper placement="top" trigger="hover" speaker={<Tooltip>{item.data.approved ? "Снять отметку" : "Отметить как согласовано"}</Tooltip>}>
                  <IconButton
                  key={`approved-${item.id}-${item.data.approved}`}
                  color="green" appearance={item.data.approved ? "primary" : "subtle"}
                  style={{marginRight: '0.25rem'}}
                  onClick={() => {
                    updateBriefSection(item.id, item.data.type, {approved: !item.data.approved});
                    handleDataChange(item.id, {approved: !item.data.approved});
                  }} icon={<CheckIcon />} />
                </Whisper>
                <Whisper placement="top" trigger="hover" speaker={<Tooltip>Удалить</Tooltip>}>
                  <IconButton
                  color="red" appearance="subtle" onClick={() => {
                    deleteBriefSection(item.id, item.data.type);
                    handleDelete(item.id)
                  }} icon={<TrashIcon />} />
                </Whisper>
            </div>
        </div>
    );
};

function App({ items, setItems }) {
  const [timeOuts, setTimeouts] = useState({});
  const ref = useRef(null);

  useEffect(() => {
    console.log(timeOuts)
  }, [timeOuts])

  function debounce(item_id, func, delay) {
    if (timeOuts[item_id]) {
      clearTimeout(timeOuts[item_id]);
    }

    setTimeouts(prev => ({ ...prev, [item_id]: setTimeout(func, delay)}));
  }

  const handleCloseAll = () => ref.current?.closeAll();
  const handleOpenAll = () => ref.current?.openAll();

  async function handleCreate (type, parent) {
    let node;

    if (type === "section") {
      node = await createSection({
        name: "Новый раздел",
        brief_id: localStorage.getItem("brief:current_edit"),
        index: 0,
      })
    } else if (type === "parameter") {
      let data = {
        name: "Новый параметр",
        have_file: false,
        have_input: false,
      }

      if (parent) {
        data.section_id = parent;
      } else {
        data.section_id = null;
        data.brief_id = localStorage.getItem("brief:current_edit");
      }

      node = await createParameter(data)
    }

    console.log(node);

    setItems(prevItems => [
      {
        id: node.id,
        text: node.name,
        droppable: type == 'section',
        parent: node.section_id ? node.section_id : 0,
        data: {
          type: type,
          have_file: node.have_file,
          have_input: node.have_input,
          have_ocr: node.have_ocr || false,
          approved: false,
          files: node.files || [],
          input_text: node.input_text || null,
          index: node.index || 0,
          updated_at: new Date(node.updated_at)
        }
      },
      ...prevItems
    ]);
  };

  async function handleDrop (newTreeData, {
    dragSource, dragSourceId, dropTargetId, dropTarget, destinationIndex, relativeIndex
  }) {

    if (dragSource.parent != dropTargetId) {
      if (dropTargetId == 0) {
        await updateBriefSection(dragSource.id, dragSource.data.type, {
          brief_id: localStorage.getItem("brief:current_edit"),
          section_id: null});
      } else {
        await updateBriefSection(dragSource.id, dragSource.data.type, {section_id: dropTargetId});
      }
    }
  
    const draggedItem = dragSource
    const siblings = newTreeData.filter(node => node.parent === (dropTargetId || 0));
  
    const updatedSiblings = siblings.filter(node => node.id !== dragSourceId);
  
    updatedSiblings.splice(relativeIndex, 0, draggedItem);
  
    updatedSiblings.forEach((node, index) => {
      node.data.index = index;
    });

    let updates = [];
  
    updatedSiblings.forEach(node => {
      updates.push({
        type: node.data.type,
        id: node.id,
        index: node.data.index,
      });
    });

    updateBriefSectionBatch(updates);
  
    setItems([...newTreeData]);
  };

  const handleTextChange = (id, value) => {
    const newTree = items.map((node) => {
      if (node.id === id) {
        return {
          ...node,
          text: value
        };
      }

      return node;
    });

    setItems(newTree);
  };

  const handleDelete = (id) => {
    const deleteIds = [
      id,
      ...getDescendants(items, id).map((node) => node.id)
    ];
    const newTree = items.filter((node) => !deleteIds.includes(node.id));

    setItems(newTree);
  };

  const handleDataChange = (id, data) => {
    const newTree = items.map((node) => {
      if (node.id === id) {
        let nodeData = { ...node.data, ...data };
        return {
          ...node,
          ...data,
          data: nodeData
        };
      }
      return node;
    });

    setItems(newTree);
  }

  const numbers = computeNumbers(items);

  return (
    <div className="brief-tree">
      <div className="brief-tree-header">
        <span>
          <Whisper placement="top" trigger="hover" speaker={<Tooltip>Свернуть всё</Tooltip>}>
            <IconButton className="brief-tree-header-icon" onClick={handleCloseAll} icon={<CollaspedFillIcon />}/>
          </Whisper>
          <Whisper placement="top" trigger="hover" speaker={<Tooltip>Развернуть всё</Tooltip>}>
          <IconButton className="brief-tree-header-icon" onClick={handleOpenAll} icon={<ArrowDownLineIcon />}/>
          </Whisper>
        </span>
        <Dropdown trigger={"hover"} className="brief-tree-header-dropdown" placement="bottomStart" title="Создать" icon={<ExpandOutlineIcon />}>
          <Dropdown.Item icon={<FolderFillIcon />} onClick={() => handleCreate("section")}>
            Раздел
          </Dropdown.Item>
          <Dropdown.Item icon={<PageIcon />} onClick={() => handleCreate("parameter")}>
            Параметр
          </Dropdown.Item>
        </Dropdown>
      </div>
      <DndProvider backend={MultiBackend} options={getBackendOptions()}>
        <Tree
          enableAnimateExpand={true}
          ref={ref}
          tree={items}
          rootId={0}
          classes={
            { 
              root: 'brief-tree-root',
              dropTarget: 'brief-tree-drop-target',
              draggingSource: 'draggingSource',
            }
          }
          canDrop={(tree, { dragSource, dropTargetId, dropTarget }) => {
              if (dragSource?.parent === dropTargetId) {
                return true;
              }
              if (dragSource.data.type == "section" && dropTarget.data.type == "section") {
                return false;
              }
            }}
          placeholderRender={(node, { depth }) => (
              <Placeholder node={node} depth={depth} />
            )}
          dragPreviewRender={customDragPreview}
          onDrop={handleDrop}
          dropTargetOffset={20}
          insertDroppableFirst={false}
          initialOpen
          sort={false}
          render={(node,
            { handleRef, depth, isOpen, onToggle }
          ) => renderTreeNode(
            node, onToggle,
            depth, isOpen,
            handleRef, handleTextChange, debounce, handleCreate,
            handleDelete, handleDataChange, numbers)}
        />
      </DndProvider>
    </div>
  );
}

export default App